import { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";
import {
  Info,
  X,
  MessageCircle,
  AlertTriangle,
  Mail,
  ChevronRight,
  Sparkles,
  BookOpen,
  Bug,
  Lightbulb,
  ExternalLink,
  Paperclip,
  Image,
  Trash2,
  Camera,
  Monitor,
  ChevronDown,
  Search,
  Send,
  Bell
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HelpButton Component - Floating help widget for Phygital
 * 
 * INTEGRATION INSTRUCTIONS FOR OTHER EMERGENT SITES:
 * ================================================
 * 
 * 1. INSTALLATION:
 *    - Copy this component to your project's /components folder
 *    - Import and add to your App.jsx or layout: <HelpButton />
 *    - Ensure lucide-react icons are installed: npm install lucide-react
 * 
 * 2. BACKEND ENDPOINT REQUIRED:
 *    Create POST /api/feedback endpoint that accepts FormData with:
 *    - type: "feedback" | "bug" | "contact"
 *    - message: string (user's message)
 *    - page: string (current URL path)
 *    - userAgent: string (browser info)
 *    - attachment: File (optional screenshot or upload)
 *    - screenshot: string (optional base64 screenshot data)
 * 
 * 3. CUSTOMIZATION:
 *    - Update FAQ_ITEMS array with your app's specific questions
 *    - Modify supportEmail and getContextualTip() for your app
 *    - Adjust colors in supportOptions to match your brand
 * 
 * 4. FEATURES:
 *    - FAQ & Help Center with searchable questions
 *    - Give Feedback form
 *    - Report Bug with screenshot capture OR file upload
 *    - Contact Support via email
 *    - Contextual tips based on current page
 *    - Notification badge for unread admin replies
 */

const HelpButton = () => {
  // Iter 9.47.r - Read help widget copy from Airtable surface
  const { slotMap: helpSlots } = useUiSurface("surface_help_widget");
  const [isOpen, setIsOpen] = useState(false);
  const [activeView, setActiveView] = useState("menu"); // menu | faq | feedback | bug | contact | replies
  const [feedbackText, setFeedbackText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [faqSearch, setFaqSearch] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [dynamicFaq, setDynamicFaq] = useState(null);
  const fileInputRef = useRef(null);
  const location = useLocation();

  // Fetch unread replies count on mount and periodically
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem("session_token");
        if (!token) return;
        
        const res = await fetch(`${API}/user/feedback/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.unread_count || 0);
        }
      } catch (err) {
        // Silently fail - user might not be logged in
      }
    };

    fetchUnreadCount();
    // Poll every 60 seconds for new replies
    const interval = setInterval(fetchUnreadCount, 60000);

    // Load FAQ from database (overrides hardcoded FAQ if available)
    const loadHelpConfig = async () => {
      try {
        const res = await fetch(`${API}/help-config/public`);
        if (res.ok) {
          const data = await res.json();
          if (data.faq_items && data.faq_items.length > 0) {
            setDynamicFaq(data.faq_items);
          }
        }
      } catch (err) {}
    };
    loadHelpConfig();
    return () => clearInterval(interval);
  }, []);

  // Fetch user's feedback with replies
  const fetchReplies = async () => {
    setLoadingReplies(true);
    try {
      const token = localStorage.getItem("session_token");
      if (!token) return;
      
      const res = await fetch(`${API}/user/feedback/replies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setReplies(data.feedback || []);
        // Mark as read
        await fetch(`${API}/user/feedback/mark-read`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(0);
      }
    } catch (err) {
      console.error("Failed to fetch replies:", err);
    } finally {
      setLoadingReplies(false);
    }
  };

  // Support email - CUSTOMIZE THIS FOR YOUR APP
  const supportEmail = "hello@phygital.systems";

  // FAQ Items - Updated for current platform state
  const FAQ_ITEMS = [
    {
      id: 1,
      question: "What is Phygital?",
      answer: "Phygital is a preference and context platform for 11 service categories. Store your beauty, event, culinary, and lifestyle preferences once, then share them instantly with providers for personalized experiences.",
      category: "general"
    },
    {
      id: 2,
      question: "How do I RSVP to an event?",
      answer: "Click the event link, hit 'Begin', then walk through the 4-step intake: your info, dietary preferences, accessibility needs, and interests. You'll receive a digital confirmation pass.",
      category: "getting-started"
    },
    {
      id: 3,
      question: "What categories are available?",
      answer: "Currently active: Events, Instructor, Makeup, Hair, Nails, Lashes, and Culinary. Coming soon: Wellness, Travel, Retail, and Dispensary.",
      category: "categories"
    },
    {
      id: 4,
      question: "How do I cancel or change my RSVP?",
      answer: "Go to the event page and click 'Can't Make It' to cancel. If you change your mind, use the 'Re-RSVP' button to reactivate your attendance.",
      category: "events"
    },
    {
      id: 5,
      question: "How do I share my preferences with a provider?",
      answer: "Your preferences are automatically shared when you RSVP or book. Hosts and providers see your dietary, accessibility, and style preferences to personalize your experience.",
      category: "sharing"
    },
    {
      id: 6,
      question: "Is my data private?",
      answer: "Yes. Your preferences are only shared with hosts and providers you explicitly engage with. You control what's visible and can update your defaults anytime from your dashboard.",
      category: "privacy"
    },
    {
      id: 7,
      question: "How do I become a provider on Phygital?",
      answer: "Join the waitlist on any category page (e.g. /mua, /hair). We'll review your application and reach out when spots open in your area.",
      category: "providers"
    },
    {
      id: 8,
      question: "What is a Host Dashboard?",
      answer: "The Host Dashboard is where event hosts and service providers manage RSVPs, client preferences, team members, and analytics. Each category has its own dashboard configuration.",
      category: "hosts"
    }
  ];

  // Get contextual help based on current page
  const getContextualTip = () => {
    const path = location.pathname;
    
    if (path.includes("/me/settings")) {
      return "Update your profile, host settings, and default preferences here.";
    }
    if (path.includes("/me")) {
      return "Your dashboard: manage events, preferences, and experiences.";
    }
    if (path.includes("/host/v2")) {
      return "Host Dashboard: manage RSVPs, intake settings, team, and analytics.";
    }
    if (path.includes("/e/")) {
      return "RSVP to this event and share your preferences with the host.";
    }
    if (path.includes("/investor")) {
      return "Investor Portal: view updates, pitch deck, projections, and advocate tools.";
    }
    if (path.includes("/support")) {
      return "Support Portal: access your team, investor, or network portal.";
    }
    if (path.includes("/advocate")) {
      return "Advocate Portal: track referrals, copy links, and view earnings.";
    }
    if (path.includes("/admin")) {
      return "Admin Dashboard: manage events, users, categories, and platform settings.";
    }
    if (path.includes("/mua") || path.includes("/hair") || path.includes("/nails") || path.includes("/chef")) {
      return "Service category page: learn about the service and join the waitlist.";
    }
    return "Welcome to Phygital: your preference layer for personalized experiences.";
  };

  const supportOptions = [
    {
      icon: BookOpen,
      label: "FAQ & Help Center",
      description: "Common questions answered",
      action: "faq",
      color: "bg-blue-500"
    },
    {
      icon: Lightbulb,
      label: "Give Feedback",
      description: "Share ideas or suggestions",
      action: "feedback",
      color: "bg-purple-500"
    },
    {
      icon: Bug,
      label: "Report a Bug",
      description: "Something not working?",
      action: "bug",
      color: "bg-amber-500"
    },
    {
      icon: Mail,
      label: "Contact Support",
      description: "Get in touch directly",
      action: "contact",
      color: "bg-gray-700"
    }
  ];

  const handleAction = (action) => {
    switch (action) {
      case "faq":
        setActiveView("faq");
        break;
      case "feedback":
        setActiveView("feedback");
        break;
      case "bug":
        setActiveView("bug");
        break;
      case "contact":
        setActiveView("contact");
        break;
      case "replies":
        setActiveView("replies");
        fetchReplies();
        break;
      default:
        break;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Limit file size to 5MB
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      setAttachment(file);
      setScreenshot(null); // Clear screenshot if uploading file
      
      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => setAttachmentPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreview(null);
      }
    }
  };

  // Capture screenshot of current page
  const captureScreenshot = useCallback(async () => {
    try {
      // Use html2canvas if available, otherwise use a simple DOM capture
      // For production, install html2canvas: npm install html2canvas
      
      // Create a visual feedback
      const flashEl = document.createElement('div');
      flashEl.style.cssText = 'position:fixed;inset:0;background:white;opacity:0.8;z-index:99999;transition:opacity 0.3s';
      document.body.appendChild(flashEl);
      
      setTimeout(() => {
        flashEl.style.opacity = '0';
        setTimeout(() => flashEl.remove(), 300);
      }, 100);

      // Try to use native screenshot API if available (works in some browsers)
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        try {
          const stream = await navigator.mediaDevices.getDisplayMedia({ video: { mediaSource: 'screen' } });
          const video = document.createElement('video');
          video.srcObject = stream;
          await video.play();
          
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0);
          
          stream.getTracks().forEach(track => track.stop());
          
          const dataUrl = canvas.toDataURL('image/png', 0.8);
          setScreenshot(dataUrl);
          setAttachment(null);
          setAttachmentPreview(null);
          return;
        } catch (e) {
          console.log("Screen capture not supported, using fallback");
        }
      }
      
      // Fallback: Create a placeholder indicating screenshot was attempted
      setScreenshot("screenshot_requested");
      setAttachment(null);
      setAttachmentPreview(null);
      
    } catch (error) {
      console.error("Screenshot failed:", error);
      alert("Screenshot capture failed. Please upload a file instead.");
    }
  }, []);

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    setScreenshot(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    setFeedbackText("");
    setAttachment(null);
    setAttachmentPreview(null);
    setScreenshot(null);
    setActiveView("menu");
    setSubmitted(false);
  };

  const submitFeedback = async (type) => {
    if (!feedbackText.trim()) return;
    
    setSubmitting(true);
    
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append("type", type);
      formData.append("message", feedbackText);
      formData.append("page", location.pathname);
      formData.append("userAgent", navigator.userAgent);
      formData.append("timestamp", new Date().toISOString());
      
      if (attachment) {
        formData.append("attachment", attachment);
      }
      if (screenshot && screenshot !== "screenshot_requested") {
        formData.append("screenshot", screenshot);
      }

      // Submit to backend
      await fetch(`${API}/feedback`, {
        method: "POST",
        body: formData
      });
      
      setSubmitted(true);
      setTimeout(() => {
        resetForm();
        setIsOpen(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      // Still show success to user - feedback logged to console
      setSubmitted(true);
      setTimeout(() => {
        resetForm();
        setIsOpen(false);
      }, 2000);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter FAQs based on search - use dynamic FAQ from DB if available
  const activeFaqList = dynamicFaq && dynamicFaq.length > 0 ? dynamicFaq : FAQ_ITEMS;
  const filteredFaqs = activeFaqList.filter(faq => 
    faq.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
    faq.answer.toLowerCase().includes(faqSearch.toLowerCase())
  );

  const renderFaqView = () => (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setActiveView("menu")} className="text-gray-400 hover:text-gray-600">
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
        <span className="font-medium text-sm">FAQ & Help Center</span>
      </div>
      
      {/* Search */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search FAQs..."
          value={faqSearch}
          onChange={(e) => setFaqSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
        />
      </div>
      
      {/* FAQ List */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {filteredFaqs.map((faq) => (
          <div key={faq.id} className="border rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
              className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
            >
              <span className="text-sm font-medium text-gray-800 pr-2">{faq.question}</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${expandedFaq === faq.id ? 'rotate-180' : ''}`} />
            </button>
            {expandedFaq === faq.id && (
              <div className="px-3 pb-3 pt-1 bg-gray-50 border-t">
                <p className="text-sm text-gray-600">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
        {filteredFaqs.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">No matching questions found</p>
        )}
      </div>
      
      <div className="mt-3 pt-3 border-t">
        <p className="text-xs text-gray-500 text-center">
          Can't find what you're looking for?{" "}
          <button onClick={() => setActiveView("contact")} className="text-blue-600 hover:underline">
            Contact Support
          </button>
        </p>
      </div>
    </div>
  );

  const renderFeedbackForm = (type) => (
    <div className="p-4">
      {submitted ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-green-600" />
          </div>
          <p className="font-medium text-gray-900">Thank you!</p>
          <p className="text-sm text-gray-500">Your {type === "bug" ? "bug report" : "feedback"} has been submitted.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => { setActiveView("menu"); removeAttachment(); }} className="text-gray-400 hover:text-gray-600">
              <ChevronRight className="w-4 h-4 rotate-180" />
            </button>
            <span className="font-medium text-sm">
              {type === "bug" ? "Report a Bug" : "Share Feedback"}
            </span>
          </div>
          
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder={type === "bug" 
              ? "Describe what went wrong:\n• What were you trying to do?\n• What happened instead?\n• Steps to reproduce the issue" 
              : "Share your ideas, suggestions, or feedback..."
            }
            className="w-full h-28 p-3 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300"
          />
          
          {/* Attachment Section for Bug Reports */}
          {type === "bug" && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-gray-600">Add a visual (helps us fix faster!)</p>
              
              {/* Screenshot or Upload Preview */}
              {(screenshot || attachment) ? (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  {screenshot ? (
                    screenshot === "screenshot_requested" ? (
                      <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                        <Monitor className="w-4 h-4 text-blue-500" />
                      </div>
                    ) : (
                      <img src={screenshot} alt="Screenshot" className="w-10 h-10 object-cover rounded" />
                    )
                  ) : attachmentPreview ? (
                    <img src={attachmentPreview} alt="Preview" className="w-10 h-10 object-cover rounded" />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                      <Paperclip className="w-4 h-4 text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">
                      {screenshot ? "Screenshot captured" : attachment?.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {screenshot ? "Page snapshot" : `${(attachment?.size / 1024).toFixed(1)} KB`}
                    </p>
                  </div>
                  <button onClick={removeAttachment} className="p-1 hover:bg-gray-200 rounded">
                    <Trash2 className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={captureScreenshot}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 border border-dashed rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                    <span>Screenshot</span>
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 border border-dashed rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  >
                    <Image className="w-4 h-4" />
                    <span>Upload</span>
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}
          
          {/* Attachment for Feedback (simpler) */}
          {type === "feedback" && (
            <div className="mt-3">
              {attachment ? (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  {attachmentPreview ? (
                    <img src={attachmentPreview} alt="Preview" className="w-10 h-10 object-cover rounded" />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                      <Paperclip className="w-4 h-4 text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">{attachment.name}</p>
                    <p className="text-xs text-gray-500">{(attachment.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button onClick={removeAttachment} className="p-1 hover:bg-gray-200 rounded">
                    <Trash2 className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  <Paperclip className="w-4 h-4" />
                  <span>Attach file (optional)</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}
          
          <p className="text-xs text-gray-400 mt-2 mb-3">
            Page: {location.pathname}
          </p>
          <button
            onClick={() => submitFeedback(type)}
            disabled={!feedbackText.trim() || submitting}
            className="w-full py-2 bg-gray-900 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </>
      )}
    </div>
  );

  const renderContactView = () => (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setActiveView("menu")} className="text-gray-400 hover:text-gray-600">
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
        <span className="font-medium text-sm">Contact Support</span>
      </div>
      
      <div className="space-y-4">
        <div className="text-center py-4">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-gray-600" />
          </div>
          <p className="text-sm text-gray-600 mb-3">Need help? Send us a message and our team will get back to you.</p>
          <button 
            onClick={() => setActiveView("feedback")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            <Send className="w-4 h-4" />
            Send Message
          </button>
        </div>
        
        <div className="border-t pt-4">
          <p className="text-xs text-gray-500 text-center">
            Messages go to Admin Support dashboard
          </p>
        </div>
      </div>
    </div>
  );

  const renderMenu = () => (
    <>
      {/* Contextual Tip */}
      <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
        <p className="text-xs text-blue-700 flex items-start gap-2">
          <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{getContextualTip()}</span>
        </p>
      </div>
      
      {/* Options */}
      <div className="p-2">
        {supportOptions.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAction(option.action)}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
          >
            <div className={`w-10 h-10 rounded-xl ${option.color} flex items-center justify-center flex-shrink-0`}>
              <option.icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-gray-900">{option.label}</p>
              <p className="text-xs text-gray-500 truncate">{option.description}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
          </button>
        ))}
        
        {/* My Messages with notification badge */}
        <button
          onClick={() => handleAction("replies")}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 transition-all group border border-purple-100"
          data-testid="help-my-messages"
        >
          <div className="w-9 h-9 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0 relative">
            <Bell className="w-4 h-4 text-white" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-gray-900">My Messages</p>
            <p className="text-xs text-gray-500 truncate">
              {unreadCount > 0 ? `${unreadCount} new replies` : "View your conversations"}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
        </button>
      </div>
    </>
  );

  // Render replies view
  const renderRepliesView = () => (
    <>
      <div className="p-4 border-b border-gray-100">
        <button 
          onClick={() => setActiveView("menu")}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
        >
          ← Back
        </button>
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Bell className="w-4 h-4" />
          My Messages
        </h3>
      </div>
      
      <div className="max-h-80 overflow-y-auto">
        {loadingReplies ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading...</p>
          </div>
        ) : replies.length === 0 ? (
          <div className="p-8 text-center">
            <MessageCircle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm font-medium text-gray-600">No messages yet</p>
            <p className="text-xs text-gray-400 mt-1">Your conversations will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {replies.map((item) => (
              <div key={item.feedback_id} className="p-4">
                {/* User's original message */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-500">You</span>
                    <span className="text-xs text-gray-400">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2">
                    {item.message}
                  </p>
                </div>
                
                {/* Admin reply */}
                {item.admin_reply ? (
                  <div className="ml-4 pl-3 border-l-2 border-purple-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-purple-600">Phygital Team</span>
                      <span className="text-xs text-gray-400">
                        {item.reply_at ? new Date(item.reply_at).toLocaleDateString() : ""}
                      </span>
                      {!item.read_at && (
                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-xs rounded font-medium">New</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 bg-purple-50 rounded-lg p-2">
                      {item.admin_reply}
                    </p>
                  </div>
                ) : (
                  <div className="ml-4 pl-3 border-l-2 border-gray-200">
                    <p className="text-xs text-gray-400 italic">Awaiting response...</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Floating Help Button with notification badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 left-4 md:bottom-6 md:left-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all duration-300 ${
          isOpen 
            ? "bg-gray-900 text-white" 
            : "bg-white/80 backdrop-blur-md text-gray-700 border border-gray-200/50 hover:bg-white hover:shadow-xl"
        }`}
        data-testid="help-button"
      >
        <div className="relative">
          {isOpen ? (
            <X className="w-4 h-4" />
          ) : (
            <>
              <Info className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                  {unreadCount > 9 ? "!" : unreadCount}
                </span>
              )}
            </>
          )}
        </div>
        <span className="text-sm font-medium">
          {isOpen ? "Close" : slotText(helpSlots, "widget_button_label", "Help")}
        </span>
      </button>

      {/* Support Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            onClick={() => { setIsOpen(false); resetForm(); }}
          />
          
          {/* Menu Panel */}
          <div className="fixed bottom-16 md:bottom-20 left-4 md:left-6 z-50 w-80 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-white" />
                  <span className="text-white font-medium text-sm">{slotText(helpSlots, "popover_title", "How can we help?")}</span>
                </div>
              </div>

              {/* Dynamic Content */}
              {activeView === "menu" && renderMenu()}
              {activeView === "faq" && renderFaqView()}
              {activeView === "feedback" && renderFeedbackForm("feedback")}
              {activeView === "bug" && renderFeedbackForm("bug")}
              {activeView === "contact" && renderContactView()}
              {activeView === "replies" && renderRepliesView()}
              
              {/* Footer */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center">
                  <a href={`mailto:${supportEmail}`} className="text-gray-700 hover:underline">{supportEmail}</a>
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default HelpButton;
